// Identity Step - Clean Kustom Style
// First step: Just email and postal code

import { useState } from 'react';
import { useCheckoutStore } from '../store/checkout';
import { Input } from '../components/Input';
import { Button } from '../components/Button';
import styles from './IdentityStep.module.css';

export function IdentityStep() {
  const setCustomer = useCheckoutStore((s) => s.setCustomer);
  const setShippingAddress = useCheckoutStore((s) => s.setShippingAddress);
  const nextStep = useCheckoutStore((s) => s.nextStep);
  const isLoading = useCheckoutStore((s) => s.isLoading);
  const setLoading = useCheckoutStore((s) => s.setLoading);
  const merchant = useCheckoutStore((s) => s.merchant);
  
  const [email, setEmail] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateEmail = (value: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  };

  const validatePostalCode = (value: string) => {
    return /^\d{4}$/.test(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const newErrors: Record<string, string> = {};
    
    if (!email) {
      newErrors.email = 'E-postadresse er påkrevd';
    } else if (!validateEmail(email)) {
      newErrors.email = 'Ugyldig e-postadresse';
    }
    
    if (!postalCode) {
      newErrors.postalCode = 'Postnummer er påkrevd';
    } else if (!validatePostalCode(postalCode)) {
      newErrors.postalCode = 'Postnummer må være 4 siffer';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 300));
    
    setCustomer({
      email,
      name: '',
      phone: '',
    });
    
    // Store postal code for next step
    setShippingAddress({
      streetAddress: '',
      postalCode,
      city: '',
      countryCode: 'NO',
    });
    
    setLoading(false);
    nextStep();
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Dine opplysninger</h1>
      <p className={styles.subtitle}>
        Adressen må være i Norge. Felt som er merket med stjerne (*) må fylles ut.
      </p>

      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.inputGroup}>
          <Input
            label="E-postadresse"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors(prev => ({ ...prev, email: '' }));
            }}
            error={errors.email}
            required
            autoComplete="email"
          />
          <Input
            label="Postnummer"
            type="text"
            inputMode="numeric"
            maxLength={4}
            value={postalCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setPostalCode(value);
              setErrors(prev => ({ ...prev, postalCode: '' }));
            }}
            error={errors.postalCode}
            required
            autoComplete="postal-code"
          />
        </div>

        <label className={styles.checkbox}>
          <input
            type="checkbox"
            className={styles.checkboxInput}
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
          />
          <span className={styles.checkboxLabel}>
            Husk mine opplysninger på denne enheten
          </span>
        </label>

        <div className={styles.buttonWrapper}>
          <Button type="submit" loading={isLoading}>
            Fortsett
          </Button>
        </div>
      </form>

      <p className={styles.terms}>
        Ved å fylle inn informasjon i kassen, godkjenner jeg{' '}
        <a 
          href={merchant?.storeInfo?.termsAndConditionsURL || '#'} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          butikkens kjøpsvilkår
        </a>{' '}
        og{' '}
        <a 
          href={merchant?.storeInfo?.privacyPolicyURL || '#'} 
          target="_blank" 
          rel="noopener noreferrer"
        >
          personvernerklæring
        </a>.
      </p>
    </div>
  );
}
